import { useCallback, useEffect, useState } from "react";
import { useProviderRegister } from "contexts/register/ProviderContext";
import { getUserService } from "services/UserServices";
import { isEmailValid } from "utils/isEmailValid";
import Input from "../../../Input";
import MaskedInput from "../../../MaskedInput";
import { ProviderToggleButton } from "../../../ProviderToggleButton";

import * as S from "./styles";
import { AutoComplete } from "components/Autocomplete";

type Props = {
  isEditing?: boolean;
};

function ProviderUserForm({ isEditing }: Props) {
  const {
    provider,
    errors,
    isPerson,
    setIsPerson,
    user,
    setUser,
    setProvider,
    addError,
    removeError,
  } = useProviderRegister();

  const [email, setEmail] = useState<string>("");

  const checkIfUserExist = useCallback(async () => {
    try {
      const userExist = await getUserService({ where: { email } });
      if (userExist.status) {
        addError("emailExists", "E-mail já registrado");
        return;
      }
    } catch {
      removeError("emailExists");
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    if (isEmailValid(email)) {
      checkIfUserExist();
    }
  }, [email, checkIfUserExist]);

  function validateEmail(event: React.ChangeEvent<HTMLInputElement>) {
    const email = event.target.value;

    if (!isEmailValid(email)) {
      addError("email", "E-mail inválido");
      return;
    }

    removeError("email");
  }

  function validateCPF(event: React.ChangeEvent<HTMLInputElement>) {
    const cpf = event.target.value.replace("_", "");
    if (cpf.length < 14) {
      addError("cpf", "CPF inválido");
      return;
    }

    removeError("cpf");
  }

  function validateRG(event: React.ChangeEvent<HTMLInputElement>) {
    const rg = event.target.value.replace("_", "");
    if (rg.length < 12) {
      addError("rg", "RG inválido");
      return;
    }

    removeError("rg");
  }

  function validateCNPJ(event: React.ChangeEvent<HTMLInputElement>) {
    const rg = event.target.value.replace("_", "");
    if (rg.length < 18) {
      addError("cnpj", "CNPJ inválido");
      return;
    }

    removeError("cnpj");
  }

  return (
    <S.Wrapper>
      <S.Form>
        <Input
          id="name"
          name="name"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setProvider((value) => {
              value.name = event.target.value;
              return value;
            });
          }}
          label="Nome fantasia"
          defaultValue={provider?.name}
          required={true}
          type="text"
          error={
            errors.name || errors["providers.name"] || errors["provider.name"]
          }
          apiErrorName={"provider.name"}
          handleApiErrorRemove={() => {
            removeError("provider.name");
          }}
        />
        <Input
          id="tradingName"
          name="tradingName"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setProvider((value) => {
              value.tradingName = event.target.value;
              return value;
            });
          }}
          defaultValue={provider?.tradingName}
          label="Razão social"
          required={true}
          type="text"
          error={errors.tradingName || errors["provider.tradingName"]}
          apiErrorName={"provider.tradingName"}
          handleApiErrorRemove={() => removeError("provider.tradingName")}
        />
        <Input
          id="managerName"
          name="managerName"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            removeError("user.name");
            setProvider((value) => {
              value.managerName = event.target.value;
              return value;
            });
          }}
          label="Nome do proprietário"
          defaultValue={provider.managerName}
          required={true}
          type="text"
          error={
            errors.managerName ||
            errors["provider.managerName"] ||
            errors["user.name"]
          }
          apiErrorName={"provider.managerName"}
          handleApiErrorRemove={() => removeError("provider.managerName")}
        />
        <Input
          id="email"
          name="email"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setEmail(event.target.value);
            setUser((value) => {
              value.email = event.target.value;
              return value;
            });
          }}
          label="Email"
          onBlur={validateEmail}
          defaultValue={user.email}
          required={true}
          type="email"
          error={
            errors.email ||
            errors["users.email"] ||
            errors["user.email"] ||
            errors.emailExists ||
            errors["client.emailExists"] ||
            errors["clients.emailExists"]
          }
          apiErrorName={"user.email"}
          handleApiErrorRemove={() => removeError("user.email")}
        />
        {!isEditing && (
          <>
            <Input
              id="password"
              name="password"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setUser((value) => {
                  value.password = event.target.value;
                  return value;
                });
              }}
              label="Senha"
              required={true}
              type="password"
              error={errors.password || errors["user.password"]}
              apiErrorName={"user.password"}
              handleApiErrorRemove={() => removeError("user.password")}
            />
          </>
        )}
        {!isEditing && (
          <ProviderToggleButton
            isPerson={isPerson}
            label="Tipo de cadastro"
            leftClick={() => {
              setIsPerson(true);
              setUser((value) => {
                value.type = 1;
                return value;
              });
            }}
            response
            rightClick={() => {
              setIsPerson(false);
              setUser((value) => {
                value.type = 2;
                return value;
              });
            }}
          />
        )}
        {isPerson && (
          <MaskedInput
            id="cpf"
            name="cpf"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setUser((value) => {
                value.cpf = event.target.value;
                return value;
              });
            }}
            defaultValue={user.cpf}
            label="CPF"
            required={true}
            type="text"
            mask="999.999.999-99"
            onBlur={validateCPF}
            error={errors.cpf || errors["user.cpf"]}
            duplicatedError={errors["users.cpf"]}
            apiErrorName={"users.cpf"}
            handleApiErrorRemove={() => removeError("users.cpf")}
          />
        )}
        {isPerson && (
          <MaskedInput
            id="rg"
            name="rg"
            onBlur={validateRG}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              validateRG(event);
              setUser((value) => {
                value.rg = event.target.value;
                return value;
              });
            }}
            defaultValue={user.rg}
            label="RG"
            required={true}
            type="text"
            mask="99.999.999-*"
            error={errors.rg || errors["user.rg"]}
            duplicatedError={errors["users.rg"]}
            apiErrorName={"users.rg"}
            handleApiErrorRemove={() => removeError("users.rg")}
          />
        )}
        {!isPerson && (
          <MaskedInput
            id="cnpj"
            name="cnpj"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setUser((value) => {
                value.cnpj = event.target.value;
                return value;
              });
            }}
            onBlur={validateCNPJ}
            defaultValue={user?.cnpj}
            label="CNPJ"
            required={true}
            type="text"
            mask="99.999.999/9999-99"
            error={errors.cnpj || errors["user.cnpj"]}
            duplicatedError={errors["users.cnpj"]}
            apiErrorName={"user.cnpj"}
            handleApiErrorRemove={() => removeError("user.cnpj")}
          />
        )}
      </S.Form>
    </S.Wrapper>
  );
}

export { ProviderUserForm };
